import { intersect } from '../../../../../submodules/common/util';
import { SelectableOption } from '../base-types';
import { SelectableOptionsSettings } from './form-field/data/selectable-options-settings';


/**
 * Costruisce un array di SelectableOption ad albero (un elemento per ogni root) a partire da un modello di albero appiattito.
 * 
 * Esampio di modello di albero appiattito in input (param 0):
 *      Array di Permission {id: string; code: string;}
 *      valorizzato così:
 *      [
 *        { id: 'C', code: 'create' },
 *        { id: 'VID_C', code: 'video:create' },
 *        { id: 'VID_D', code: 'video:delete' },
 *        { id: 'USR_C', code: 'users:create' },
 *        { id: 'USR_PRM_R', code: 'users:permissions:read' }
 *      ]
 *
 *     In questo caso mi aspetto in input (nei param 1 e 2)
 *     - idFieldName valorizzato con 'id'
 *     - labelFieldName valorizzato con 'code'
 * 
 * Esempio di output:
 *     [
 *        {
 *           "id":"C",
 *           "label":"create",
 *           "children":null
 *        },
 *        {
 *           "id":null,
 *           "label":"video",
 *           "children":[
 *              {
 *                 "id":"VID_C",
 *                 "label":"video:create",
 *                 "children":null
 *              },
 *              {
 *                 "id":"VID_D",
 *                 "label":"video:delete",
 *                 "children":null
 *              }
 *           ]
 *        },
 *        {
 *           "id":null,
 *           "label":"users",
 *           "children":[
 *              {
 *                 "id":"USR_C",
 *                 "label":"users:create",
 *                 "children":null
 *              },
 *              {
 *                 "id":null,
 *                 "label":"users:permissions",
 *                 "children":[
 *                    {
 *                       "id":"USR_PRM_R",
 *                       "label":"users:permissions:read",
 *                       "children":null
 *                    }
 *                 ]
 *              }
 *           ]
 *        }
 *     ]
 * 
 * NOTE: l'id viene valorizzato solo per le foglie, visto che per i nodi intermedi non è significativo
 */
export const buildTreeSelectableOptionsFromFlatTreeModel = (flatTreeModel: any[], idFieldName: string, labelFieldName): SelectableOption[] => {
    let result = []
    let nodesMap: {[key:string]: SelectableOption} = {}; //mappa di appoggio per costruire il result
    flatTreeModel.forEach((el) => {
       let prefixForNextNode = '';
       el[labelFieldName].split(':').forEach((frag: string, i: number) => {
          const isRoot = prefixForNextNode==''
          const isLeaf = (i == el[labelFieldName].split(':').length-1)
          const currentNodeKey = isRoot ? frag : `${prefixForNextNode}:${frag}`
          if(!nodesMap[currentNodeKey]){
             const currentNode = {id: isLeaf?el[idFieldName]:null, label: currentNodeKey, children: isLeaf?null:[]}
             nodesMap[currentNodeKey]=currentNode;
             if(!isRoot){
                let parent = nodesMap[prefixForNextNode];
                parent.children.push(currentNode);
             } else
                result.push(currentNode)
          }
          prefixForNextNode = currentNodeKey
       });
    });
    return result;
}

export const buildSelectableOptions = (options: SelectableOption[] | Object[], optionsSettings?: SelectableOptionsSettings, principal?: any): {selectableOptions: SelectableOption[], 
                                                                                    selectableOptionsMap: {[key: string]: SelectableOption} | {[key: number]: SelectableOption} 
                                                                                   } => {
   //console.log('buildSelectableOptions - options: '+ JSON.stringify(options) + ' - principal: '+ JSON.stringify(principal));
   optionsSettings = optionsSettings || {}
   const selectableOptionIdFieldName = optionsSettings.selectableOptionIdFieldName || 'id';
   const selectableOptionLabelBuilderFunction = optionsSettings.selectableOptionLabelBuilderFunction;
   const selectableOptionLabelFieldName = optionsSettings.selectableOptionLabelFieldName || 'label';
   const selectableOptionShortLabelBuilderFunction = optionsSettings.selectableOptionShortLabelBuilderFunction;
   const selectableOptionShortLabelFieldName  = optionsSettings.selectableOptionShortLabelFieldName || 'shortLabel'
   const selectableOptionChildrenFieldName = optionsSettings.selectableOptionChildrenFieldName || 'children';
   let selectableOptionsMap = {};
   const mapToSelectableOption = (input: any): SelectableOption => {
      const children: SelectableOption[] = input[selectableOptionChildrenFieldName] 
                                             ? input[selectableOptionChildrenFieldName].map(el=>mapToSelectableOption(el)) 
                                             : null;
      const out = {
         id: input[selectableOptionIdFieldName], 
         label: selectableOptionLabelBuilderFunction ? selectableOptionLabelBuilderFunction(input) 
                                                      : input[selectableOptionLabelFieldName], 
         shortLabel: selectableOptionShortLabelBuilderFunction ? selectableOptionShortLabelBuilderFunction(input)
                     : input[selectableOptionShortLabelFieldName] ? input[selectableOptionShortLabelFieldName]
                     : selectableOptionLabelBuilderFunction ? selectableOptionLabelBuilderFunction(input) 
                     : input[selectableOptionLabelFieldName],
         icon: input.icon,
         hidden: input.hidden || !isAuthorized(input.requiredAuthorities, principal),
         disabled: input.disabled,
         children: children,
         navigable: input.navigable,  //TODO: spostare
         url: input.url,              //TODO: spostare
         showInPrivateMenu: input.showInPrivateMenu //TODO: spostare
      };
      selectableOptionsMap[out.id]=out;
      return out;
   }
   const selectableOptions = (options as any[]).map(el=>mapToSelectableOption(el))
   return {selectableOptions: selectableOptions, selectableOptionsMap: selectableOptionsMap}
}

/**
* Formatta un number introducendo i separatori delle migliaia
*/
export const formatInteger = input => {
 input = input.toString();
 var pattern = /(-?\d+)(\d{3})/;
 while (pattern.test(input)) {
   input = input.replace(pattern, "$1 $2");
 }
 return input;
}

const isAuthorized = (requiredAuthorities, userProfile) => {
   if(!userProfile)
      return false;
   else{
      // console.log('userProfile :>> ', "userProfile presente");
      // console.log('requiredAuthorities :>> ', requiredAuthorities);
      // console.log('userProfile.authorities :>> ', userProfile.authorities);
      // console.log('intersect(requiredAuthorities,userProfile.authorities).length :>> ', intersect(requiredAuthorities,userProfile.authorities).length);

      return !userProfile?false:intersect(requiredAuthorities,userProfile.authorities).length==requiredAuthorities.length;
   }
      
}
